import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";

const pop = {
  whileHover: { scale: 1.08 },
  whileTap: { scale: 0.9 },
  transition: { type: "spring", stiffness: 500, damping: 15 },
};

export const Navbar = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.process, href: "#process" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.blog, href: "#blog" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0a0a0b]/90 backdrop-blur-xl border-b border-zinc-800" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#top" data-testid="logo-link" className="flex items-center gap-2">
            <span className="bg-red-600 text-white font-display font-bold text-3xl px-2 py-0.5 tracking-tight shadow-[0_0_20px_rgba(239,68,68,0.5)]">RM</span>
            <span className="font-display font-medium uppercase tracking-tight text-white text-2xl leading-none">
              {t.footer.brand} - {t.footer.city}
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <motion.a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                {...pop}
                className="inline-block text-sm font-semibold text-zinc-300 hover:text-red-500 transition-colors uppercase tracking-wide"
              >
                {l.label}
              </motion.a>
            ))}
            <LanguageToggle />
            <motion.a
              href="tel:+359892600604"
              data-testid="nav-call-btn"
              {...pop}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-5 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors shadow-[0_0_20px_rgba(239,68,68,0.4)]"
            >
              <Phone size={16} strokeWidth={2.5} /> {t.nav.call}
            </motion.a>
          </nav>

          <div className="flex items-center gap-4 md:hidden">
            <LanguageToggle />
            <button
              data-testid="mobile-menu-toggle"
              className="text-white"
              onClick={() => setOpen(!open)}
              aria-label={t.nav.menu}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#0a0a0b] border-b border-zinc-800">
          <nav className="flex flex-col px-4 py-4 gap-1">
            {links.map((l) => (
              <motion.a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                whileTap={{ scale: 0.94 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                className="py-3 text-zinc-200 font-semibold uppercase tracking-wide border-b border-zinc-800"
              >
                {l.label}
              </motion.a>
            ))}
            <motion.a
              href="tel:+359892600604"
              whileTap={{ scale: 0.94 }}
              transition={{ type: "spring", stiffness: 500, damping: 15 }}
              className="mt-3 flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-3 font-bold uppercase tracking-wide"
            >
              <Phone size={16} /> +359 892 600 604
            </motion.a>
          </nav>
        </div>
      )}
    </header>
  );
};
